import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { Spin, Alert, Tabs, Typography, Switch, Button, List, Badge, Modal, InputNumber, Select, Radio } from 'antd';
import { useCarwashSlotsManager } from 'src/hooks/useCarwashSlotsManager';
import { AVAILABLE_SLOT_DURATIONS } from 'src/constants/slotConstants';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

export const CarWashSlots = forwardRef(({ value, onChange, form, description, greeting, onSaveSuccess }, ref) => {
  const {
    isLoading,
    isSaving,
    error,
    days,
    selectedDay,
    setSelectedDay,
    slotsForSelectedDay,
    isDayEnabled,
    areAllSlotsEnabled,
    slotDurationByDay,
    startTimeByDay,
    endTimeByDay,
    openSheet,
    setOpenSheet,
    selectedDuration,
    setSelectedDuration,
    applyDurationToAll,
    setApplyDurationToAll,
    selectedStartTime,
    setSelectedStartTime,
    applyStartToAll,
    setApplyStartToAll,
    selectedEndTime,
    setSelectedEndTime,
    applyEndToAll,
    setApplyEndToAll,
    selectedCapacity,
    setSelectedCapacity,
    applyCapacityToAll,
    setApplyCapacityToAll,
    usedCapacity,
    orphanSlots,
    isDirty,
    setIsDirty,
    getDateForDay,
    loadData,
    handleSave,
    buildCarwashSlotsPayload,
    handleToggleAllSlotsForAllDays,
    handleCapacityPress,
    handleToggleActive,
    handleToggleAllSlots,
    handleModalSave,
    generateTimes
  } = useCarwashSlotsManager(value, onChange);

  const saveAll = async () => {
    await handleSave({ ...form?.getFieldsValue(), description, greeting });
    if (onSaveSuccess) onSaveSuccess();
  };

  useImperativeHandle(ref, () => ({
    saveAll,
    setIsDirty,
    buildCarwashSlotsPayload: () => buildCarwashSlotsPayload()
  }), [saveAll, setIsDirty, buildCarwashSlotsPayload]);

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}><Spin size='large' /><Text>Loading Configuration...</Text></div>;
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message='Error'
          description={error.message}
          type='error'
          showIcon
          action={
            error.retryable && <Button onClick={() => loadData(true)} disabled={isLoading}>Retry</Button>
          }
        />
      </div>
    );
  }

  const renderHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', borderBottom: '1px solid #DDD' }}>
      <Title level={4}>Carwash Slots</Title>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Text>All Days</Text>
        <Switch
          checked={areAllSlotsEnabled}
          onChange={handleToggleAllSlotsForAllDays}
        />
      </div>
    </div>
  );

  const renderSlotSettings = () => {
    if (!selectedDay) return null;
    return (
      <div style={{ padding: '15px', borderBottom: '1px solid #DDD' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <Title level={5}>{selectedDay} Settings</Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Text>Enable Day</Text>
            <Switch
              checked={isDayEnabled}
              onChange={handleToggleAllSlots}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '15px', padding: '10px 0', flexWrap: 'wrap' }}>
          <ConfigItem label='Duration' value={`${slotDurationByDay[selectedDay]} min`} onClick={() => setOpenSheet('duration')} />
          <ConfigItem label='Start' value={startTimeByDay[selectedDay]} onClick={() => setOpenSheet('start')} />
          <ConfigItem label='End' value={endTimeByDay[selectedDay]} onClick={() => setOpenSheet('end')} />
          <ConfigItem label='Default Capacity' value={slotsForSelectedDay[0]?.capacity ?? 'N/A'} onClick={() => setOpenSheet('default_capacity')} />
        </div>
      </div>
    );
  };

  const ConfigItem = ({ label, value, onClick }) => (
    <div>
      <Text style={{ color: '#555', display: 'block', marginBottom: '5px' }}>{label}</Text>
      <Button onClick={onClick}>
        {value}
      </Button>
    </div>
  );

  const renderSlotsList = () => {
    if (!selectedDay) return <Text style={{ textAlign: 'center', marginTop: '20px', display: 'block' }}>Select a day to see the slots</Text>;

    const allSlots = [
      ...orphanSlots.before.slots.map(s => ({ ...s, id: `orphan-before-${s.time}`, isOrphan: true, active: false, duration: 60 })),
      ...slotsForSelectedDay,
      ...orphanSlots.after.slots.map(s => ({ ...s, id: `orphan-after-${s.time}`, isOrphan: true, active: false, duration: 60 }))
    ];

    return (
      <List
        dataSource={allSlots}
        rowKey='id'
        renderItem={(item) => <SlotRow item={item} />}
        locale={{ emptyText: 'No slots configured for this day.' }}
        style={{ padding: '0 15px' }}
      />
    );
  };

  const SlotRow = ({ item }) => {
    const date = getDateForDay(selectedDay);
    const currentUsedCapacity = usedCapacity[`${date}-${item.time}`] || 0;

    const isOrphan = !!item.isOrphan;

    // Calculate end time based on start time + duration
    const calculateEndTime = (startTime, duration) => {
      const [hours, minutes] = startTime.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + duration;
      const endHours = Math.floor(totalMinutes / 60);
      const endMinutes = totalMinutes % 60;
      return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
    };

    const slotDuration = item.duration || slotDurationByDay[selectedDay] || 60;
    const endTime = calculateEndTime(item.time, slotDuration);
    const timeDisplay = `${item.time} - ${endTime}`;

    return (
      <List.Item
        style={isOrphan ? { backgroundColor: '#fff2f0', fontStyle: 'italic' } : {}}
        actions={!isOrphan
          ? [
            <Badge key='badge' count={`Used: ${currentUsedCapacity}`} style={{ backgroundColor: currentUsedCapacity > 0 ? '#1890ff' : '#d9d9d9' }} />,
            <Switch
              key='switch'
              checked={item.active}
              onChange={(active) => handleToggleActive(selectedDay, item.id, active)}
            />
            ]
          : [<Badge key='badge' count={`Used: ${item.capacity}`} style={{ backgroundColor: '#ff4d4f' }} />]}
      >
        <List.Item.Meta
          title={<Text style={{ fontSize: '16px', color: isOrphan ? '#cf1322' : 'inherit' }}>{timeDisplay}</Text>}
          description={
            isOrphan
              ? <Text type='danger'>Orphan</Text>
              : (
                <Button type='link' style={{ padding: 0 }} onClick={() => handleCapacityPress(item.id, item.capacity)}>
                  Capacity: {item.capacity}
                </Button>
                )
          }
        />
      </List.Item>
    );
  };

  const renderModal = () => {
    if (!openSheet) return null;

    const isTimeSheet = openSheet === 'start' || openSheet === 'end';
    const isDurationSheet = openSheet === 'duration';
    const isCapacitySheet = openSheet === 'capacity' || openSheet === 'default_capacity';
    const isDefault = openSheet === 'default_capacity';
    const times = isTimeSheet ? generateTimes(openSheet === 'end', startTimeByDay[selectedDay]) : [];

    let title = '';
    if (isDurationSheet) title = 'Select Duration';
    if (isTimeSheet) title = openSheet === 'start' ? 'Select Start Time' : 'Select End Time';
    if (isCapacitySheet) title = isDefault ? 'Set Default Capacity' : 'Set Capacity';

    return (
      <Modal
        title={title}
        visible={!!openSheet}
        onOk={handleModalSave}
        onCancel={() => setOpenSheet(null)}
        okText='Save'
        cancelText='Cancel'
      >
        {isDurationSheet && (
          <DurationSelector
            selectedDuration={selectedDuration}
            setSelectedDuration={setSelectedDuration}
            applyToAll={applyDurationToAll}
            setApplyToAll={setApplyDurationToAll}
          />
        )}
        {isTimeSheet && (
          <TimeSelector
            times={times}
            selectedTime={openSheet === 'start' ? selectedStartTime : selectedEndTime}
            onSelectTime={openSheet === 'start' ? setSelectedStartTime : setSelectedEndTime}
            applyToAll={openSheet === 'start' ? applyStartToAll : applyEndToAll}
            setApplyToAll={openSheet === 'start' ? setApplyStartToAll : setApplyEndToAll}
          />
        )}
        {isCapacitySheet && (
          <CapacitySelector
            selectedCapacity={selectedCapacity}
            setSelectedCapacity={setSelectedCapacity}
            applyToAll={applyCapacityToAll}
            setApplyToAll={setApplyCapacityToAll}
            isDefault={openSheet === 'default_capacity'}
          />
        )}
      </Modal>
    );
  };

  const DurationSelector = ({ selectedDuration, setSelectedDuration, applyToAll, setApplyToAll }) => (
    <div>
      <Radio.Group onChange={(e) => setSelectedDuration(e.target.value)} value={selectedDuration}>
        {AVAILABLE_SLOT_DURATIONS.map(duration => (
          <Radio.Button key={duration} value={duration}>{duration} min</Radio.Button>
        ))}
      </Radio.Group>
      <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #DDD' }}>
        <Switch checked={applyToAll} onChange={setApplyToAll} />
        <Text style={{ marginLeft: '10px' }}>Apply to all days</Text>
      </div>
    </div>
  );

  const TimeSelector = ({ times, selectedTime, onSelectTime, applyToAll, setApplyToAll }) => (
    <div>
      <Select
        style={{ width: '100%' }}
        value={selectedTime}
        onChange={onSelectTime}
        listHeight={200}
        getPopupContainer={trigger => trigger.parentNode}
      >
        {times.map((time) => (
          <Option key={time} value={time}>{time}</Option>
        ))}
      </Select>
      <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #DDD' }}>
        <Switch checked={applyToAll} onChange={setApplyToAll} />
        <Text style={{ marginLeft: '10px' }}>Apply to all days</Text>
      </div>
    </div>
  );

  const CapacitySelector = ({ selectedCapacity, setSelectedCapacity, applyToAll, setApplyToAll, isDefault }) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '20px 0' }}>
        <Button onClick={() => setSelectedCapacity(Math.max(1, selectedCapacity - 1))}>-</Button>
        <InputNumber value={selectedCapacity} style={{ margin: '0 20px' }} readOnly />
        <Button onClick={() => setSelectedCapacity(selectedCapacity + 1)}>+</Button>
      </div>
      {isDefault && (
        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #DDD' }}>
          <Switch checked={applyToAll} onChange={setApplyToAll} />
          <Text style={{ marginLeft: '10px' }}>Apply to all days</Text>
        </div>
      )}
    </div>
  );

  const renderFooter = () => (
    <div style={{ position: 'sticky', bottom: 0, padding: '15px', backgroundColor: '#FFF', borderTop: '1px solid #DDD', textAlign: 'right' }}>
      <Button type={isDirty ? 'primary' : 'default'} onClick={saveAll} loading={isSaving} disabled={!isDirty}>
        {isSaving ? 'Saving...' : `Save Changes ${isDirty ? '*' : ''}`}
      </Button>
    </div>
  );

  return (
    <div style={{ border: '1px solid #DDD', borderRadius: '2px' }}>
      {renderHeader()}
      <Tabs activeKey={selectedDay} onChange={setSelectedDay} style={{ padding: '0 15px' }}>
        {days.map(day => (
          <TabPane tab={day} key={day} />
        ))}
      </Tabs>
      {renderSlotSettings()}
      {renderSlotsList()}
      {renderModal()}
      {renderFooter()}
    </div>
  );
});

export default CarWashSlots;
